import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';

import Destinations from './components/Destinations';
import Testimonials from './components/Testimonials';
import About from './components/About';
import Contact from './components/Contact';
import Auth from './components/Auth';
import OurDifference from './components/OurDifference';
import Services from './components/Services';

import HomeServices from './components/HomeServices';
import DiscoverIndia from './components/DiscoverIndia';
import FeaturedDestinations from './components/FeaturedDestinations';
import CustomisedDestination from './components/CustomisedDestination';

import Stats from './components/Stats';
import ScrollToTop from './components/ScrollToTop';
import ScrollProgressBar from './components/ScrollProgressBar';
import ScrollReveal from './components/ScrollReveal';
import LenisProvider from './components/LenisProvider';
import Footer from './components/Footer';
import WhatsAppWidget from './components/WhatsAppWidget';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';

import AdminDashboard from './components/AdminDashboard';
import SEO from './components/SEO';
import ReactGA from 'react-ga4';
import { motion, AnimatePresence } from 'framer-motion';
// Icons and animations
import { X, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

// Initialize Google Analytics 4
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
} else {
  console.warn("Google Analytics: REACT_APP_GA_MEASUREMENT_ID is not defined. Tracking is disabled.");
}


const WelcomePopup = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    // Show popup every time website loads
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Celebration blaster!
      const duration = 2 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(function () {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          // Auto-close popup after confetti finishes
          setIsVisible(false);
          return;
        }

        const isMobile = window.innerWidth <= 768;
        const particleCount = (isMobile ? 12 : 50) * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }, 1500); // Show after 1.5s
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0, transition: { when: "afterChildren", duration: 0.3 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, scale: 1, y: 0,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    },
    exit: { 
      opacity: 0, scale: 0.95, y: 10,
      transition: { staggerChildren: 0.15, staggerDirection: -1, when: "afterChildren", duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div 
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md"
        >
          <motion.div
            variants={modalVariants}
            className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm relative overflow-hidden text-center p-4 sm:p-6"
          >
            {/* Background Accent */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-orange-300"></div>
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>

            <motion.button
              variants={itemVariants}
              onClick={handleClose}
              className="absolute top-4 right-4 sm:top-5 sm:right-5 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-50"
            >
              <X size={18} />
            </motion.button>

            {/* Fancy Anniversary Celebration Visual - Integrated */}
            <motion.div variants={itemVariants} className="relative w-full h-40 flex items-center justify-center bg-gradient-to-br from-orange-600 via-orange-500 to-orange-400 rounded-3xl overflow-hidden shadow-xl border-[3px] border-white/20">
              {/* Dense Stars & Dots Background */}
              <div className="absolute inset-0 opacity-80 pointer-events-none">
                <div className="absolute top-4 left-4 text-white animate-pulse">⭐</div>
                <div className="absolute top-8 right-8 text-yellow-200 animate-bounce">⭐</div>
                <div className="absolute bottom-6 left-10 text-white animate-pulse">⭐</div>
                <div className="absolute bottom-3 right-4 text-yellow-100 animate-bounce">⭐</div>
                <div className="absolute top-1/2 left-4 w-1.5 h-1.5 bg-white rounded-full"></div>
                <div className="absolute top-1/4 right-1/3 w-2 h-2 bg-white rounded-full"></div>
                <div className="absolute bottom-1/3 left-1/4 w-1 h-1 bg-white rounded-full"></div>
                <div className="absolute top-1/3 right-4 w-2 h-2 bg-white rounded-full"></div>
                <div className="absolute bottom-4 left-1/2 w-1 h-1 bg-white rounded-full"></div>
              </div>

              {/* Main '25' Typography */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="relative flex items-center justify-center">
                  <span className="text-[60px] md:text-[70px] font-black text-white leading-none drop-shadow-[3px_3px_0_rgba(0,0,0,0.3)] select-none">
                    25
                    <span className="text-lg ml-1 align-top mt-3 text-white/90">th</span>
                  </span>
                </div>

                {/* Anniversary Script */}
                <div className="flex flex-col items-center -mt-2">
                  <span className="text-2xl text-white font-serif italic tracking-wide drop-shadow-md" style={{ fontFamily: "'Dancing Script', cursive" }}>
                    Anniversary
                  </span>

                  {/* Celebration Box */}
                  <div className="mt-1 flex items-center gap-2">
                    <div className="w-1 h-2 bg-white transform -skew-x-12"></div>
                    <span className="bg-white/10 backdrop-blur-sm text-white text-[7px] font-black uppercase tracking-[0.3em] px-2 py-0.5 border border-white/20">
                      Celebration
                    </span>
                    <div className="w-1 h-2 bg-white transform skew-x-12"></div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xl animate-bounce">🎈</span>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Home = () => (
  <>
    <SEO 
      title="Bespoke Luxury Travel & Holidays" 
      description="Celebrating 25 years of crafting bespoke, luxury travel experiences. Discover the world with customized tour packages, flight bookings, visa assistance, and corporate travel programs with the warmth of Indian hospitality." 
      canonicalPath="" 
    />
    <Hero />
    <ScrollReveal direction="up" delay={0.05}>
      <Stats />
    </ScrollReveal>
    <ScrollReveal direction="up" delay={0.05}>
      <DiscoverIndia />
    </ScrollReveal>
  </>
);

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [showToast, setShowToast] = React.useState(false);

  // Track page views in Google Analytics on route change
  React.useEffect(() => {
    if (GA_MEASUREMENT_ID) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location]);

  // Check if current user is an admin
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isSuperAdmin = user?.is_superuser;

  // Fire continuous falling confetti on non-admin routes - CRANKED UP (Disabled on mobile for performance)
  React.useEffect(() => {
    if (isAdminRoute) return;
    if (window.innerWidth <= 768) return; // Completely disable continuous confetti on mobile

    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 50 };
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(function () {
      // Continuous high-quality sparkle effect (confetti)
      const particleCount = 20;

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.05, 0.25), y: Math.random() - 0.2 },
        colors: ['#f97316', '#FFD700', '#ffffff', '#D4AF37'],
        scalar: randomInRange(0.5, 1.2),
        gravity: 1.0
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.75, 0.95), y: Math.random() - 0.2 },
        colors: ['#f97316', '#FFD700', '#ffffff', '#D4AF37'],
        scalar: randomInRange(0.5, 1.2),
        gravity: 1.0
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isAdminRoute]);

  React.useEffect(() => {
    if (isAdminRoute) return;

    // Reset toast to false to ensure a fresh, smooth animation on page change
    setShowToast(false);

    // 4.5s delay on Home or Destinations to wait for 25th welcome popup, immediately (100ms) on all other pages
    const isDelayedPage = location.pathname === '/' || location.pathname === '/destinations';
    const delay = isDelayedPage ? 4500 : 100;

    const timer = setTimeout(() => {
      setShowToast(true);
    }, delay);

    // Hide toast 5 seconds after it is shown
    const hideTimer = setTimeout(() => {
      setShowToast(false);
    }, delay + 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, [location.pathname, isAdminRoute]);

  // Big blast on every page navigation (optimized count on mobile)
  React.useEffect(() => {
    if (!isAdminRoute) {
      const isMobile = window.innerWidth <= 768;
      const count = isMobile ? 40 : 200;
      const defaults = {
        origin: { y: 0.7 },
        colors: ['#f97316', '#FFD700', '#ffffff', '#D4AF37'],
        zIndex: 9999,
        scalar: isMobile ? 1.0 : 1.5,
        spread: isMobile ? 50 : 80,
        startVelocity: isMobile ? 40 : 60
      };

      confetti({ ...defaults, particleCount: count, origin: { x: 0, y: 0.8 } });
      confetti({ ...defaults, particleCount: count, origin: { x: 1, y: 0.8 } });
    }
  }, [location.pathname, isAdminRoute]);

  // If user is admin and trying to access a non-admin route, redirect to admin panel
  if (isSuperAdmin && !isAdminRoute) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="app-container">
      {!isAdminRoute && <ScrollProgressBar />}
      {!isAdminRoute && <Navbar />}
      {!isAdminRoute && <WelcomePopup />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/services" element={<Services />} />

          <Route path="/our-difference" element={<OurDifference />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/about" element={<Navigate to="/about-us" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/customised-destination" element={<CustomisedDestination />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </main>
      {!isAdminRoute && <WhatsAppWidget />}
      {!isAdminRoute && <Footer />}

      <AnimatePresence>
        {!isAdminRoute && showToast && (
          <div className="fixed top-24 sm:top-28 left-4 right-4 mx-auto max-w-sm lg:left-1/2 lg:right-auto lg:-translate-x-1/2 lg:transform lg:ml-[153px] lg:mx-0 lg:w-auto z-[9999]">
            <motion.div
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={() => window.open('https://www.youtube.com/@TheSunKissedPod', '_blank')}
              className="bg-slate-900/95 backdrop-blur-md border border-orange-500/30 text-white p-4 px-5 rounded-2xl shadow-2xl cursor-pointer hover:border-orange-500 transition-all duration-300 group flex items-center gap-3 w-full"
            >
              <div className="w-8 h-8 bg-orange-500 rounded-xl flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M12 2C8.69 2 6 4.69 6 8v5c0 3.31 2.69 6 6 6s6-2.69 6-6V8c0-3.31-2.69-6-6-6zm4 11c0 2.21-1.79 4-4 4s-4-1.79-4-4V8c0-2.21 1.79-4 4-4s4 1.79 4 4v5zm-4 8c-3.07 0-5.5-2.43-5.5-5.5h1.5C8 18.2 9.8 20 12 20s4-1.8 4-4.5h1.5c0 3.07-2.43 5.5-5.5 5.5z"/>
                </svg>
              </div>
              <div className="flex-1 space-y-0.5 pr-6">
                <h4 className="font-bold text-sm tracking-tight leading-tight">Discover Bharat in a different way.</h4>
                <p className="text-slate-300 text-xs font-medium">Watch our latest podcast now!</p>
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowToast(false);
                }}
                className="text-slate-400 hover:text-white transition-colors absolute top-3 right-3"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <Router>
      <LenisProvider />
      <ScrollToTop />
      <AppLayout />
    </Router>
  );
}

export default App;
